// Invoice redirect (2026-07-25) — keep the project workspace chrome on invoice
// detail.
//
// Invoice detail now lives INSIDE its project's sub-nav
// (/projects/:projectId/invoice/:invoiceId — note the tab segment is `invoice`,
// singular) so the project workspace sub-nav tabs stay put when a rep opens a
// deposit/final invoice from an order or the Invoice tab.
// This wrapper keeps the legacy top-level `/invoices/:id` URL (emails /
// bookmarks / global search / InvoicingPage) working: it resolves the invoice's
// project and forwards into context.
//
// Defensive fallback: if the invoice carries no project id we render the
// standalone detail page rather than stranding the user. Reuses the detail
// page's ["invoices", id, "detail"] query key so the fetch is shared, not
// duplicated.
//
// Mirrors OrderRedirect / PresentationRedirect (Nav Part 1, PR #160).
import { Navigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { InvoiceDetailPage } from "@/pages/InvoiceDetailPage";

interface RedirectInvoice {
  invoice: { projectId: string | null };
}

export function InvoiceRedirect() {
  const { id = "" } = useParams<{ id: string }>();
  const { data, isError } = useQuery({
    queryKey: ["invoices", id, "detail"],
    queryFn: () => api<RedirectInvoice>(`/invoices/${id}`),
    enabled: id.length > 0,
  });

  if (isError) return <Navigate to="/finance/invoicing" replace />;
  if (!data) {
    return (
      <div className="px-6 py-24 text-center text-sm text-brown-300">
        Opening invoice…
      </div>
    );
  }

  // No project id → render standalone, don't strand the user.
  if (!data.invoice.projectId) return <InvoiceDetailPage />;

  return (
    <Navigate
      to={`/projects/${data.invoice.projectId}/invoice/${id}`}
      replace
    />
  );
}
